/**
 * Quote Proposal Service — Full API Implementation
 * 
 * Token mapping:
 * - toolboxToken (was igtoken): localStorage.getItem('toolboxToken')
 * - amsToken (was NCToken): localStorage.getItem('amsToken')
 */

import axios, { AxiosProgressEvent } from 'axios';

/* ── Types ── */

export interface ClientSearchResult {
  name: string;
  databaseId: string;
  eMail: string;
  insuredType: number;
}

export interface PolicyData {
  databaseId: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  carrier: string;
  premium: number;
  lineOfBusiness: string;
  isQuote: boolean;
  status: string;
  totalPremium?: number;
}

export interface CoverageDetail {
  coverageType: string;
  limit: string;
  deductible: string;
  premium: number;
}

export interface DriverInfo {
  name: string;
  licenseNumber: string;
  dateOfBirth: string;
  status: string;
}

export interface VehicleInfo {
  year: string;
  make: string;
  model: string;
  vin: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role?: string;
  signature?: string;
  signatureImage?: string;
}

export interface TeamMemberListResponse {
  members: TeamMember[];
  templateId?: string;
  customTemplateId?: number;
  customTemplateName?: string;
}

export interface CompanyData {
  companyId: string;
  name: string;
  website: string;
  aboutUs: string;
  address: string;
  logoUrl: string;
  defaultQuoteType: string;
  autoSaveDrafts: boolean;
  displayCurrency: string;
  emailNotification: boolean;
  primaryColour: string;
  secondaryColour: string;
  overview: string;
  vision: string;
  mission: string;
  templateId?: string | number;
  customTemplateId?: number;
  customTemplateName?: string;
  teamMembers?: string; // JSON string
  signature?: string;
}

export interface SettingsTeamMember {
  name: string;
  role: string;
  profilePhoto: string;
  signature?: string;
  selectedOption?: 'draw' | 'type' | 'upload';
  typedSignature?: string;
  drawnSignature?: string;
  uploadedSignature?: string | null;
}

export interface CustomTemplate {
  id: number;
  name: string;
  title?: string;
  parentTemplateId: number;
  images: string[] | string;
  description?: string;
}

export interface CustomBlock {
  id: string;
  title: string;
  content: string;
  order: number;
  visible: boolean;
}

export interface DraftPayload {
  files?: File[];
  amsFileIds?: string;
  folderId?: string;
  insuredId?: string;
  fileNames?: string[];
  driveFileIds?: string[];
  driveFolderIds?: string[];
  fileIds?: string[];
  policyDetails?: string;
  quoteDetails?: string;
  documentType?: string;
  templateId?: string;
  videoUrl?: string;
  transactionId?: string;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  fileURL?: string;
  proposalTitle?: string;
  selectedTeamMember?: string;
  selectedProposalMembers?: string;
  extraDocDetails?: string;
  customBlockDetails?: string;
}

export interface DraftResponse {
  transactionId: string;
  filesId: string[] | string;
  files: string[];
  success: boolean;
}

export interface DraftData {
  clientId: string;
  clientName: string;
  clientEmail: string;
  fileName: string;
  fileURL: string;
  videoUrl: string;
  proposalTitle: string;
  selectedTeamMember: string;
  selectedProposalMembers: any[];
  extraDocDetails: any[];
  templateId: string;
  customTemplateId: number;
  customTemplateName: string;
  transactionId: string;
  fileIds: string[];
}

export interface ProposalHistoryItem {
  fileName: string;
  submittedDate: string;
  status: number;
  transactionId: string;
  clientName: string;
  clientEmail: string;
  proposalTitle: string;
  clientId: string;
  agentName: string;
  quoteDetails?: any;
}

export interface FileListItem {
  databaseId: string;
  name: string;
  fileOrFolder: 'file' | 'folder';
  size?: number;
  creatorName?: string;
  createDate?: string;
  changeDate?: string;
  downloadUrl?: string;
  parentId?: string;
}

export interface FileListResponse {
  currentFolderId: string;
  name: string;
  files: FileListItem[];
}

export interface SubscriptionStatus {
  success: boolean;
  stripeStatus: string;
  isActive: boolean;
}

/* ── Helper: Get tokens ── */
const getToolboxToken = (): string => localStorage.getItem('toolboxToken') || '';
const getAmsToken = (): string => localStorage.getItem('amsToken') || '';

/* ── Helper: Create auth headers ── */
const authHeaders = (useAmsToken = false) => ({
  Authorization: `Bearer ${useAmsToken ? getAmsToken() : getToolboxToken()}`,
});

/* ── Helper: Re-throw errors so callers can handle per-screen UX. ── */
const rethrowError = (error: any) => {
  throw error;
};

/* ═══════════════════════════════════════════════════════════════
   1. CLIENT SEARCH
   ═══════════════════════════════════════════════════════════════ */

export async function searchClients(searchText: string): Promise<ClientSearchResult[]> {
  if (!searchText.trim()) return [];
  
  try {
    const response = await axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist`, {
      params: {
        searchText,
        nowCertsToken: getAmsToken(),
      },
      headers: authHeaders(),
    });
    return response.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   2. CLIENT POLICIES & QUOTES
   ═══════════════════════════════════════════════════════════════ */

export async function getClientPolicies(insuredId: string): Promise<{
  policies: PolicyData[];
  quotes: PolicyData[];
}> {
  try {
    const response = await axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insuredpolicies`, {
      params: {
        insuredId,
        nowCertsToken: getAmsToken(),
      },
      headers: authHeaders(),
    });
    
    const data = response.data || [];
    const policies = data.filter((p: PolicyData) => !p.isQuote);
    const quotes = data.filter((p: PolicyData) => p.isQuote);
    
    return { policies, quotes };
  } catch (error) {
    rethrowError(error);
    return { policies: [], quotes: [] };
  }
}

/* ═══════════════════════════════════════════════════════════════
   3. POLICY COVERAGE DETAILS
   ═══════════════════════════════════════════════════════════════ */

export async function getPolicyCoverages(policyId: string): Promise<CoverageDetail[]> {
  try {
    const response = await axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/getpolicyCoverageslist`, {
      params: {
        policyId,
        ncToken: getAmsToken(),
      },
      headers: authHeaders(),
    });
    return response.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   4. DRIVER LIST
   ═══════════════════════════════════════════════════════════════ */

export async function getDriverList(insuredId: string): Promise<DriverInfo[]> {
  try {
    const response = await axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/driverlist`, {
      params: {
        insuredId,
        ncToken: getAmsToken(),
      },
      headers: authHeaders(),
    });
    return response.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   5. VEHICLE LIST
   ═══════════════════════════════════════════════════════════════ */

export async function getVehicleList(insuredDatabaseId: string): Promise<VehicleInfo[]> {
  try {
    const response = await axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/vehicles`, {
      params: {
        insuredDatabaseId,
        ncToken: getAmsToken(),
      },
      headers: authHeaders(),
    });
    return response.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   6. CLIENT PROFILE FILE BROWSER (AMS Drive)
   ═══════════════════════════════════════════════════════════════ */

export async function getInsuredFilesList(
  insuredId: string,
  folderId?: string
): Promise<FileListResponse | null> {
  try {
    const params: Record<string, string> = { insuredId };
    if (folderId) params.folderId = folderId;
    
    const response = await axios.get(
      `${process.env.REACT_APP_Nowcerts_Base_Url}/api/Insured/GetInsuredFilesList`,
      {
        params,
        headers: authHeaders(true), // Uses amsToken
      }
    );
    return response.data?.data || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   7. COMPANY / TEAM SETTINGS
   ═══════════════════════════════════════════════════════════════ */

export async function getCompanyHistory(): Promise<CompanyData | null> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/getcompanyhistory?origin=true`,
      { headers: authHeaders() }
    );
    return response.data || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

export async function saveCompanySettings(settings: Partial<CompanyData>): Promise<boolean> {
  try {
    await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/companyhistory`,
      settings,
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

/* ═══════════════════════════════════════════════════════════════
   7b. LOGO / PROFILE PHOTO UPLOAD
   ═══════════════════════════════════════════════════════════════ */

export async function uploadCompanyLogo(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/uploadlogo`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data?.fileURL || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

export async function uploadTeamProfilePhoto(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/upload/TeamProfilePhoto`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data?.fileURL || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   7c. CUSTOM TEMPLATES
   ═══════════════════════════════════════════════════════════════ */

export async function getCustomTemplates(): Promise<CustomTemplate[]> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/GetCustomTemplatesByCompanyId`,
      { headers: authHeaders() }
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

export async function createCustomTemplate(formData: FormData): Promise<boolean> {
  try {
    await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/customtemplate`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

export async function deleteCustomTemplate(id: number): Promise<boolean> {
  try {
    await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/custom-template/delete/${id}`,
      {},
      { headers: authHeaders() }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

/* ═══════════════════════════════════════════════════════════════
   7d. CUSTOM BLOCKS SAVE / DELETE
   ═══════════════════════════════════════════════════════════════ */

export async function saveCustomBlock(formData: FormData): Promise<boolean> {
  try {
    await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/custom-block/save`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

export async function deleteCustomBlock(id: number): Promise<boolean> {
  try {
    await axios.delete(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/custom-block/${id}`,
      { headers: authHeaders() }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

/* ═══════════════════════════════════════════════════════════════
   8. TEAM MEMBERS LIST
   ═══════════════════════════════════════════════════════════════ */

export async function getTeamMembers(): Promise<TeamMemberListResponse | null> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/getteammember`,
      { headers: authHeaders() }
    );
    const data = response.data?.response || response.data;
    return {
      members: data?.members || data || [],
      templateId: data?.templateId,
      customTemplateId: data?.customTemplateId,
      customTemplateName: data?.customTemplateName,
    };
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   9. CUSTOM BLOCKS FOR TEMPLATE
   ═══════════════════════════════════════════════════════════════ */

export async function getCustomBlocks(templateId: string): Promise<CustomBlock[]> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/custom-blocks`,
      {
        params: { templateId },
        headers: authHeaders(),
      }
    );
    return response.data?.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   10. CREATE / UPDATE DRAFT
   ═══════════════════════════════════════════════════════════════ */

export async function createOrUpdateDraft(
  payload: DraftPayload,
  onUploadProgress?: (progress: number) => void
): Promise<DraftResponse | null> {
  try {
    const formData = new FormData();
    
    // Handle file uploads (step 0)
    if (payload.files && payload.files.length > 0) {
      payload.files.forEach((file) => {
        formData.append('files[]', file);
      });
    }
    
    // Drive file selection
    if (payload.amsFileIds) formData.append('amsFileIds', payload.amsFileIds);
    if (payload.folderId) formData.append('folderId', payload.folderId);
    formData.append('nc_token', getAmsToken());
    if (payload.insuredId) formData.append('insuredId', payload.insuredId);
    
    // File names
    payload.fileNames?.forEach((name) => formData.append('fileNames[]', name));
    payload.driveFileIds?.forEach((id) => formData.append('driveFileIds[]', id));
    payload.driveFolderIds?.forEach((id) => formData.append('driveFolderIds[]', id));
    
    // For subsequent steps (no re-upload)
    payload.fileIds?.forEach((id) => formData.append('fileIds[]', id));
    
    // Always append these
    formData.append('policyDetails', payload.policyDetails || '');
    formData.append('quoteDetails', payload.quoteDetails || '');
    formData.append('documentType', payload.documentType || '');
    formData.append('templateId', payload.templateId || '');
    formData.append('videoUrl', payload.videoUrl || '');
    formData.append('transactionId', payload.transactionId || '');
    formData.append('clientId', payload.clientId || '');
    formData.append('clientName', payload.clientName || '');
    formData.append('clientEmail', payload.clientEmail || '');
    formData.append('fileURL', payload.fileURL || '');
    formData.append('proposalTitle', payload.proposalTitle || '');
    formData.append('selectedTeamMember', payload.selectedTeamMember || '');
    formData.append('selectedProposalMembers', payload.selectedProposalMembers || '[]');
    formData.append('extraDocDetails', payload.extraDocDetails || '[]');
    formData.append('customBlockDetails', payload.customBlockDetails || '[]');
    
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/draft`,
      formData,
      {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (event.total && onUploadProgress) {
            const progress = Math.round((event.loaded * 100) / event.total);
            onUploadProgress(progress);
          }
        },
      }
    );
    
    return response.data;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   11. LOAD DRAFT
   ═══════════════════════════════════════════════════════════════ */

export async function getDraftData(transactionId: string): Promise<DraftData | null> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/getdraftdata`,
      {
        params: { transactionId },
        headers: authHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   11.5 UPLOAD QUOTE DOCUMENT (Immediate upload in Step 1)
   ═══════════════════════════════════════════════════════════════ */

export interface UploadedQuoteFile {
  fileId: string;
  fileURL: string;
  fileName: string;
}

export async function uploadQuoteDocument(
  file: File,
  onUploadProgress?: (progress: number) => void
): Promise<UploadedQuoteFile | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);
    
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/uploadquotedoc`,
      formData,
      {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (event.total && onUploadProgress) {
            const progress = Math.round((event.loaded * 100) / event.total);
            onUploadProgress(progress);
          }
        },
      }
    );
    
    return {
      fileId: response.data.fileId || response.data.filesId || '',
      fileURL: response.data.fileURL || response.data.fileUrl || response.data.files?.[0] || '',
      fileName: response.data.fileName || file.name,
    };
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   12. UPLOAD EXTRA DOCUMENT
   ═══════════════════════════════════════════════════════════════ */

export async function uploadExtraDocument(
  file: File,
  transactionId: string
): Promise<{ fileURL: string; fileName: string } | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transactionId', transactionId);
    
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/uploadextradoc`,
      formData,
      {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return {
      fileURL: response.data.fileURL || response.data.fileUrl,
      fileName: response.data.fileName,
    };
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   13. PROPOSAL HISTORY LIST
   ═══════════════════════════════════════════════════════════════ */

export async function getProposalHistory(): Promise<ProposalHistoryItem[]> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/nc-quote-proposal-history?origin=true`,
      { headers: authHeaders() }
    );
    return response.data || [];
  } catch (error) {
    rethrowError(error);
    return [];
  }
}

/* ═══════════════════════════════════════════════════════════════
   14. UPDATE PROPOSAL TITLE
   ═══════════════════════════════════════════════════════════════ */

export async function updateProposalTitle(
  transactionId: string,
  proposalTitle: string
): Promise<boolean> {
  try {
    await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/updateProposalTitle`,
      null,
      {
        params: { transactionId, proposalTitle },
        headers: authHeaders(),
      }
    );
    return true;
  } catch (error) {
    rethrowError(error);
    return false;
  }
}

/* ═══════════════════════════════════════════════════════════════
   15. SEND TO AMS
   ═══════════════════════════════════════════════════════════════ */

export async function sendProposalToAms(
  pdfBlob: Blob,
  fileName: string,
  insuredId: string,
  transactionId: string
): Promise<{ success: boolean; fileDatabaseId?: string }> {
  try {
    const formData = new FormData();
    formData.append('file', new File([pdfBlob], fileName, { type: 'application/pdf' }));
    formData.append('fileName', fileName);
    formData.append('nc_token', getAmsToken());
    formData.append('insuredId', insuredId);
    formData.append('transactionId', transactionId);
    
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/sendtoams`,
      formData,
      {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return {
      success: true,
      fileDatabaseId: response.data?.fileDatabaseId,
    };
  } catch (error) {
    rethrowError(error);
    return { success: false };
  }
}

/* ═══════════════════════════════════════════════════════════════
   16. SIGN DOCUMENT VIA RABBITSIGN
   ═══════════════════════════════════════════════════════════════ */

export async function getIgTokenForSigning(): Promise<string | null> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_Nowcerts_Base_Url}/api/IGToken/GetToken`,
      { headers: authHeaders(true) } // Uses amsToken
    );
    return response.data || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

export function openRabbitSignWindow(params: {
  igtoken: string;
  fileId: string;
  insuredId: string;
  signerEmail: string;
  signerName: string;
}): void {
  const { igtoken, fileId, insuredId, signerEmail, signerName } = params;
  const fromEmail = localStorage.getItem('userName') || '';
  
  const url = new URL(`${process.env.REACT_APP_RabbitSign_Url}/RabbitSignExternal`);
  url.searchParams.set('igtoken', igtoken);
  url.searchParams.set('theme', 'dark');
  url.searchParams.set('fileId', fileId);
  url.searchParams.set('insuredId', insuredId);
  url.searchParams.set('signers', `${signerEmail},${signerName},1`);
  url.searchParams.set('fromEmail', fromEmail);
  url.searchParams.set('emailSubject', 'Please sign this document');
  url.searchParams.set('emailBody', 'Please sign this document');
  
  window.open(url.toString(), '_blank');
}

/* ═══════════════════════════════════════════════════════════════
   17. VERIFY SUBSCRIPTION
   ═══════════════════════════════════════════════════════════════ */

export async function verifySubscription(): Promise<SubscriptionStatus> {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/NCQuoteProposal/verify`,
      {},
      { headers: authHeaders() }
    );
    
    const data = response.data;
    return {
      success: data.success === true,
      stripeStatus: data.stripe_status || '',
      isActive: data.success && data.stripe_status === 'active',
    };
  } catch (error) {
    rethrowError(error);
    return { success: false, stripeStatus: '', isActive: false };
  }
}

/* ═══════════════════════════════════════════════════════════════
   18. GET MOMENTUM TOKEN (for subscription redirect)
   ═══════════════════════════════════════════════════════════════ */

export async function getMomentumToken(): Promise<string | null> {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_UserService_Url}/api/user/getmomentumtoken`,
      { headers: authHeaders() }
    );
    return response.data?.token || response.data || null;
  } catch (error) {
    rethrowError(error);
    return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   LEGACY EXPORTS (for backward compatibility)
   ═══════════════════════════════════════════════════════════════ */

export interface TemplateOption {
  id: string;
  name: string;
}

export interface SignatureOption {
  id: string;
  name: string;
  previewUrl?: string;
}

/**
 * Fetch toolbox token from API and store in localStorage.
 * Uses amsToken (NCToken) to authenticate and retrieve the toolboxToken.
 */
export async function fetchToolboxToken(): Promise<string | null> {
  try {
    const amsToken = getAmsToken();
    if (!amsToken) {
      console.warn('No amsToken found, cannot fetch toolbox token');
      return null;
    }

    const response = await axios.get<{
      userName: string;
      workEmail: string;
      token: string;
      igToken: string | null;
      userId: string;
      orgName: string;
      companyName: string;
      roleId: number;
      avatarName: string;
      firstName: string;
      lastName: string;
      iconURL: string | null;
      hasGIGMarket: boolean;
      headerText: string;
      slugURL: string;
      tenantId: number;
      userProfileId: number;
      refreshToken: string;
      expiry: string;
      nowCertsAgentId: string;
      nowCertsAgencyId: string;
      isTalkToAMSEnabled: boolean;
    }>(`${process.env.REACT_APP_AccountAMP_Url}/api/account/toolboxtoken`, {
      params: { nctoken: amsToken },
    });

    const { token } = response.data;
    if (token) {
      localStorage.setItem('toolboxToken', token);
      return token;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch toolbox token:', error);
    return null;
  }
}

export async function generatePreview(proposalId: string): Promise<{
  proposalId: string;
  previewUrl: string;
  status: 'processing' | 'ready' | 'error';
}> {
  return { proposalId, previewUrl: '', status: 'processing' };
}
